// @flow
import { createSelector } from 'reselect';

import { selectAuthenticatedUserInfos } from '@gandi/authentication';

import { selectProducts, selectCartUUID } from '@gandi/cart';

import type { Organization } from '@gandi/redux-sharing';

import type { FullState } from 'src/types';

import type { Instance, InstanceSummary } from '../../Studio/modules/instances';
import { selectShopCurrent } from '../organization/selectors';

export const STUDIO_STEP_ROUTES: {
  [key:
    | 'overview'
    | 'account'
    | 'convert'
    | 'configure'
    | 'firstBuy'
    | 'accountChoice']: string,
} = {
  overview: `/cart/:cartId/overview`,
  account: `/studio/:cartId/account`,
  convert: `/studio/:vhost/convert`,
  configure: '/cart/:cartId/configure/domain-contacts',
  firstBuy: `/cart/:cartId/configure/account/create`,
  accountChoice: `/cart/:cartId/configure/account-choice`,
};

const selectHasGandiStudioProduct: (state: FullState) => boolean =
  createSelector([selectProducts], products => {
    return products.some(product => product.payload?.shopping?.studio);
  });

export const selectIsStudioUser: (state: FullState) => boolean = createSelector(
  [selectAuthenticatedUserInfos],
  userInfos => {
    if (!userInfos) return false;
    return userInfos.roles.includes('gandi-studio');
  },
);

export const selectIsEligibleStudioUser: (state: FullState) => boolean =
  createSelector(
    [selectAuthenticatedUserInfos, selectIsStudioUser],
    (userInfos, isStudioUser) => {
      if (!isStudioUser) return false;
      if (!userInfos?.email_validation_deadline) return false;

      const minDeadline = new Date();
      minDeadline.setDate(minDeadline.getDate() - 30);

      const deadline = new Date(userInfos.email_validation_deadline);
      return deadline > minDeadline;
    },
  );

export const selectStudioFlowExpectedStep: (
  state: FullState,
  flag: boolean,
) => string | void = createSelector(
  [
    (state: FullState) => selectHasGandiStudioProduct(state),
    (state: FullState) => selectShopCurrent(state),
    (_, flag) => flag,
    selectCartUUID,
  ],
  (
    hasStudioProduct: boolean,
    user: void | Organization,
    flag: boolean,
    cartId?: string,
  ) => {
    if (!cartId || !hasStudioProduct) {
      return undefined;
    }

    if (user?.phone) {
      return `/cart/${cartId}/overview`;
    }
    return flag
      ? `/cart/${cartId}/configure/account/create`
      : `/studio/${cartId}/account`;
  },
);

export function getTrialId(instances: Array<InstanceSummary>): string | void {
  if (instances.length > 0) {
    return instances[0].trial ? instances[0].id : undefined;
  }
  return undefined;
}

export function getInstancesFqdn(instances: Instance): string | void {
  if (instances) {
    return instances.vhosts.length > 0 ? instances.vhosts[0].fqdn : undefined;
  }
  return undefined;
}

export type StudioStep = $Keys<typeof STUDIO_STEP_ROUTES>;

function patternToRegex(pattern: string) {
  const regexStr = pattern.replace(/:([^/]+)/g, '([^/]+)');
  return new RegExp(regexStr);
}

export function isStudioStepUrl(urlOriginal: string): boolean {
  return Object.values(STUDIO_STEP_ROUTES).some(pattern =>
    patternToRegex(String(pattern)).test(urlOriginal),
  );
}

const paymentReturnPath = /.*\/payment-return/;
const awaitingPaymentPath = /.*\/awaiting-payment\/.*/;

export function shouldExcludeFromStudioRedirect(url: string): boolean {
  return awaitingPaymentPath.test(url) || paymentReturnPath.test(url);
}
